import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Multiselect,
  Textarea,
  Select,
  StatusIndicator,
  Flashbar,
  Box,
  Badge,
  RadioGroup,
  Link,
  FormField,
  ExpandableSection,
} from '@cloudscape-design/components';
import { submitFeedback, updateFeedback, getFeedback, submitReply } from '../services/s3Service';

const DECISION_OPTIONS = [
  { label: 'Recommend Approval', value: 'recommend_approval' },
  { label: 'Recommend Rejection', value: 'recommend_rejection' },
  { label: 'Needs Review', value: 'needs_review' },
];

const ReviewerFeedbackPanel = ({ fundRequestId, validationDecision, validationChecks }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [selectedValidations, setSelectedValidations] = useState([]);
  const [disagreementReason, setDisagreementReason] = useState('');
  const [recommendedDecision, setRecommendedDecision] = useState(null);
  const [flashItems, setFlashItems] = useState([]);
  const [replyDisposition, setReplyDisposition] = useState(null);
  const [replyExplanation, setReplyExplanation] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [canReplyToFeedback, setCanReplyToFeedback] = useState(false);
  const [allFeedback, setAllFeedback] = useState([]);
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadExistingFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFeedback(fundRequestId);
      if (data.feedback) {
        setExistingFeedback(data.feedback);
      }
      setCanReplyToFeedback(data.canReplyToFeedback || false);
      setAllFeedback(data.allFeedback || []);
    } catch (err) {
      console.error('Failed to load feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [fundRequestId]);

  useEffect(() => {
    loadExistingFeedback();
  }, [loadExistingFeedback]);

  const validationOptions = (validationChecks || []).map((check) => ({
    label: check.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    value: check,
  }));

  const isDisagreeValid =
    feedbackType === 'DISAGREE' &&
    selectedValidations.length > 0 &&
    disagreementReason.trim().length > 0 &&
    recommendedDecision !== null;

  const canSubmit = feedbackType === 'AGREE' || isDisagreeValid;

  const startEditing = () => {
    if (existingFeedback) {
      setFeedbackType(existingFeedback.feedbackType);
      setSelectedValidations(
        (existingFeedback.disagreementValidations || []).map((v) => ({
          label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          value: v,
        }))
      );
      setDisagreementReason(existingFeedback.disagreementReason || '');
      setRecommendedDecision(
        existingFeedback.recommendedDecision
          ? DECISION_OPTIONS.find((o) => o.value === existingFeedback.recommendedDecision) || null
          : null
      );
    }
    setIsEditing(true);
    setFlashItems([]);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setFeedbackType(null);
    setSelectedValidations([]);
    setDisagreementReason('');
    setRecommendedDecision(null);
    setFlashItems([]);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setFlashItems([]);

      const disagreeValidations = feedbackType === 'DISAGREE' ? selectedValidations.map((s) => s.value) : null;
      const disagreeReason = feedbackType === 'DISAGREE' ? disagreementReason : null;
      const recDecision = feedbackType === 'DISAGREE' && recommendedDecision ? recommendedDecision.value : null;

      if (isEditing) {
        await updateFeedback(fundRequestId, feedbackType, validationDecision, disagreeValidations, disagreeReason, recDecision);
      } else {
        await submitFeedback(fundRequestId, feedbackType, validationDecision, disagreeValidations, disagreeReason, recDecision);
      }

      setFlashItems([
        {
          type: 'success',
          content: isEditing ? 'Feedback updated successfully.' : 'Feedback submitted successfully.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
          id: 'feedback-success',
        },
      ]);

      setIsEditing(false);
      // Reload to get the full record from server
      loadExistingFeedback();
    } catch (err) {
      if (err.status === 409) {
        setFlashItems([
          {
            type: 'info',
            content: 'Feedback was already submitted for this fund request.',
            dismissible: true,
            onDismiss: () => setFlashItems([]),
            id: 'feedback-conflict',
          },
        ]);
        loadExistingFeedback();
      } else {
        setFlashItems([
          {
            type: 'error',
            content: `Failed to ${isEditing ? 'update' : 'submit'} feedback: ${err.message}`,
            dismissible: true,
            onDismiss: () => setFlashItems([]),
            id: 'feedback-error',
            action: <Button onClick={handleSubmit}>Retry</Button>,
          },
        ]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDecision = (value) => {
    const option = DECISION_OPTIONS.find((o) => o.value === value);
    return option ? option.label : value;
  };

  const isReplyValid = replyDisposition !== null && replyExplanation.trim().length > 0;

  const handleReplySubmit = async (targetFeedback) => {
    if (!isReplyValid || !targetFeedback) return;

    try {
      setSubmittingReply(true);
      setFlashItems([]);

      const result = await submitReply(
        targetFeedback.fundRequestId,
        targetFeedback.reviewerId,
        replyDisposition,
        replyExplanation
      );

      const flashMessages = [
        {
          type: 'success',
          content: 'Reply submitted successfully.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
          id: 'reply-success',
        },
      ];

      setFlashItems(flashMessages);
      setReplyDisposition(null);
      setReplyExplanation('');
      setReplyTargetId(null);
      loadExistingFeedback();
    } catch (err) {
      if (err.status === 409) {
        setFlashItems([
          {
            type: 'info',
            content: 'A reply has already been submitted for this feedback.',
            dismissible: true,
            onDismiss: () => setFlashItems([]),
            id: 'reply-conflict',
          },
        ]);
        loadExistingFeedback();
      } else {
        setFlashItems([
          {
            type: 'error',
            content: `Failed to submit reply: ${err.message}`,
            dismissible: true,
            onDismiss: () => setFlashItems([]),
            id: 'reply-error',
            action: <Button onClick={() => handleReplySubmit(targetFeedback)}>Retry</Button>,
          },
        ]);
      }
    } finally {
      setSubmittingReply(false);
    }
  };

  const buildSummary = () => {
    if (loading) return 'Loading...';
    if (!existingFeedback) {
      if (allFeedback.length > 0) {
        return `No feedback from you · ${allFeedback.length} other reviewer(s)`;
      }
      return 'No feedback submitted';
    }
    const icon = existingFeedback.feedbackType === 'AGREE' ? '✅' : '⚠️';
    const date = existingFeedback.submittedAt
      ? new Date(existingFeedback.submittedAt).toLocaleDateString()
      : '';
    let summary = `${icon} You: ${existingFeedback.feedbackType}`;
    if (existingFeedback.feedbackType === 'DISAGREE' && existingFeedback.recommendedDecision) {
      const rec = DECISION_OPTIONS.find((o) => o.value === existingFeedback.recommendedDecision);
      summary += ` → ${rec ? rec.label : existingFeedback.recommendedDecision}`;
    }
    if (date) summary += ` · ${date}`;
    const otherCount = allFeedback.filter((fb) => fb.reviewerId !== existingFeedback.reviewerId).length;
    if (otherCount > 0) summary += ` · +${otherCount} other reviewer(s)`;
    return summary;
  };

  if (loading) {
    return (
      <ExpandableSection
        variant="container"
        headerText="Reviewer Feedback"
        headerDescription={buildSummary()}
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
      >
        <StatusIndicator type="loading">Loading feedback...</StatusIndicator>
      </ExpandableSection>
    );
  }

  // Render a single feedback card (used for both own and other reviewers' feedback)
  const renderFeedbackCard = (fb, isOwnFeedback) => (
    <Container
      key={fb.reviewerId}
      header={
        <Header variant="h3" actions={isOwnFeedback ? <Button onClick={startEditing}>Edit</Button> : null}>
          {isOwnFeedback ? 'Your Feedback' : `Feedback by ${fb.reviewerAlias || fb.reviewerId}`}
        </Header>
      }
    >
      <SpaceBetween size="s">
        <Box>
          <Box variant="awsui-key-label">Feedback</Box>
          <StatusIndicator type={fb.feedbackType === 'AGREE' ? 'success' : 'warning'}>
            {fb.feedbackType}
          </StatusIndicator>
        </Box>
        {fb.recommendedDecision && (
          <Box>
            <Box variant="awsui-key-label">Recommended decision</Box>
            <Box>{formatDecision(fb.recommendedDecision)}</Box>
          </Box>
        )}
        {fb.disagreementValidations && fb.disagreementValidations.length > 0 && (
          <Box>
            <Box variant="awsui-key-label">Disagreed validations</Box>
            <SpaceBetween direction="horizontal" size="xs">
              {fb.disagreementValidations.map((v) => (
                <Badge key={v} color="red">{v.replace(/_/g, ' ')}</Badge>
              ))}
            </SpaceBetween>
          </Box>
        )}
        {fb.disagreementReason && (
          <Box>
            <Box variant="awsui-key-label">Reason</Box>
            <Box>{fb.disagreementReason}</Box>
          </Box>
        )}
        {fb.submittedAt && (
          <Box>
            <Box variant="awsui-key-label">Submitted</Box>
            <Box>{new Date(fb.submittedAt).toLocaleString()}</Box>
          </Box>
        )}

        {/* Reply display */}
        {fb.replyDisposition != null && (
          <Container header={<Header variant="h3">Reply</Header>}>
            <SpaceBetween size="s">
              <Box>
                <Box variant="awsui-key-label">Disposition</Box>
                <Badge color={fb.replyDisposition === 'NOT_AN_ISSUE' ? 'blue' : 'red'}>
                  {fb.replyDisposition === 'NOT_AN_ISSUE' ? 'Not an issue' : 'Is an issue'}
                </Badge>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Explanation</Box>
                <Box>{fb.replyExplanation}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Responder</Box>
                <Box>{fb.replyResponderAlias}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Replied at</Box>
                <Box>{new Date(fb.replyTimestamp).toLocaleString()}</Box>
              </Box>
              {fb.simTicketUrl != null && (
                <Box>
                  <Box variant="awsui-key-label">SIM Ticket</Box>
                  <Link href={fb.simTicketUrl} external variant="primary" target="_blank" rel="noopener noreferrer">
                    Create SIM Ticket
                  </Link>
                </Box>
              )}
            </SpaceBetween>
          </Container>
        )}

        {/* Reply form — DISAGREE, no reply, user has permission */}
        {fb.feedbackType === 'DISAGREE' &&
          fb.replyDisposition == null &&
          canReplyToFeedback &&
          replyTargetId === fb.reviewerId && (
            <Container header={<Header variant="h3">Reply to Feedback</Header>}>
              <SpaceBetween size="m">
                <FormField label="Disposition">
                  <RadioGroup
                    value={replyDisposition}
                    onChange={({ detail }) => setReplyDisposition(detail.value)}
                    items={[
                      { value: 'NOT_AN_ISSUE', label: 'Not an issue' },
                      { value: 'IS_AN_ISSUE', label: 'Yes, it is an issue' },
                    ]}
                  />
                </FormField>
                <FormField
                  label="Explanation"
                  errorText={
                    replyExplanation.length > 0 && replyExplanation.trim().length === 0
                      ? 'Explanation cannot be empty or whitespace only.'
                      : undefined
                  }
                  constraintText="Required — explain your disposition."
                >
                  <Textarea
                    value={replyExplanation}
                    onChange={({ detail }) => setReplyExplanation(detail.value)}
                    placeholder="Provide an explanation for your reply..."
                    rows={3}
                  />
                </FormField>
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    onClick={() => handleReplySubmit(fb)}
                    loading={submittingReply}
                    disabled={!isReplyValid || submittingReply}
                  >
                    Submit Reply
                  </Button>
                  <Button onClick={() => { setReplyTargetId(null); setReplyDisposition(null); setReplyExplanation(''); }}>
                    Cancel
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            </Container>
          )}

        {/* Reply button — DISAGREE, no reply, user has permission, form not open */}
        {fb.feedbackType === 'DISAGREE' &&
          fb.replyDisposition == null &&
          canReplyToFeedback &&
          replyTargetId !== fb.reviewerId && (
            <Button onClick={() => { setReplyTargetId(fb.reviewerId); setReplyDisposition(null); setReplyExplanation(''); }}>
              Reply
            </Button>
          )}
      </SpaceBetween>
    </Container>
  );

  // Read-only state: feedback already submitted and not editing
  if (existingFeedback && !isEditing) {
    // Other reviewers' feedback (exclude own)
    const otherFeedback = allFeedback.filter((fb) => fb.reviewerId !== existingFeedback.reviewerId);

    return (
      <ExpandableSection
        variant="container"
        headerText="Reviewer Feedback"
        headerDescription={buildSummary()}
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
      >
        <SpaceBetween size="l">
          <Flashbar items={flashItems} />
          {renderFeedbackCard(existingFeedback, true)}
          {otherFeedback.map((fb) => renderFeedbackCard(fb, false))}
        </SpaceBetween>
      </ExpandableSection>
    );
  }

  // Submission / edit state
  return (
    <ExpandableSection
      variant="container"
      headerText="Reviewer Feedback"
      headerDescription={buildSummary()}
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
    >
    <Container
      header={
        <Header variant="h3" actions={isEditing ? <Button onClick={cancelEditing}>Cancel</Button> : null}>
          Reviewer Feedback {isEditing ? '(Editing)' : ''}
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Flashbar items={flashItems} />
        <Box>
          <Box variant="awsui-key-label">Do you agree with Chronos's validation decision?</Box>
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant={feedbackType === 'AGREE' ? 'primary' : 'normal'}
              onClick={() => {
                setFeedbackType('AGREE');
                setSelectedValidations([]);
                setDisagreementReason('');
                setRecommendedDecision(null);
              }}
            >
              Agree
            </Button>
            <Button
              variant={feedbackType === 'DISAGREE' ? 'primary' : 'normal'}
              onClick={() => setFeedbackType('DISAGREE')}
            >
              Disagree
            </Button>
          </SpaceBetween>
        </Box>

        {feedbackType === 'DISAGREE' && (
          <SpaceBetween size="s">
            <Select
              selectedOption={recommendedDecision}
              onChange={({ detail }) => setRecommendedDecision(detail.selectedOption)}
              options={DECISION_OPTIONS}
              placeholder="What should the decision be?"
            />
            <Multiselect
              selectedOptions={selectedValidations}
              onChange={({ detail }) => setSelectedValidations(detail.selectedOptions)}
              options={validationOptions}
              placeholder="Select validation checks you disagree with"
              filteringType="auto"
            />
            <Textarea
              value={disagreementReason}
              onChange={({ detail }) => setDisagreementReason(detail.value)}
              placeholder="Explain why you disagree with the validation decision..."
              rows={3}
            />
            {!isDisagreeValid && (selectedValidations.length === 0 || disagreementReason.trim().length === 0 || !recommendedDecision) && (
              <StatusIndicator type="warning">
                A recommended decision, validation selection, and reason are all required.
              </StatusIndicator>
            )}
          </SpaceBetween>
        )}

        {feedbackType && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit || submitting}
          >
            {isEditing ? 'Update Feedback' : 'Submit Feedback'}
          </Button>
        )}
      </SpaceBetween>
    </Container>
    {/* Show other reviewers' feedback below the submission form for admins */}
    {allFeedback.length > 0 && (
      <SpaceBetween size="l">
        {allFeedback.map((fb) => renderFeedbackCard(fb, false))}
      </SpaceBetween>
    )}
    </ExpandableSection>
  );
};

export default ReviewerFeedbackPanel;
